import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, Check, CheckCircle2, Clock3, MessageCircle, Pencil, Zap } from "lucide-react";
import Calendar from "./Calendar";
import { HOURS, SERVICES, TIME_SLOTS, WA_NUMBER } from "../../data/site";
import { trackEvent } from "../../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Hizmet", "Tarih", "Saat", "Bilgiler", "Onay"];
const EMPTY = { service: "", date: null, time: "", name: "", phone: "", guests: 1, contact: "whatsapp", note: "" };

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookingWizard() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState([]);
  const [calMode, setCalMode] = useState("quick");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!form.date) return;
    axios
      .get(`${API}/bookings/slots`, { params: { date: toIso(form.date) } })
      .then(({ data }) => setBusy(data.booked || []))
      .catch(() => setBusy([]));
  }, [form.date]);

  useEffect(() => {
    if (form.time && busy.includes(form.time)) set({ time: "" });
  }, [busy]); // eslint-disable-line react-hooks/exhaustive-deps

  const phoneOk = /^0?5\d{9}$/.test(form.phone.replace(/[\s()-]/g, ""));
  const stepValid = [
    form.service !== "",
    form.date !== null,
    form.time !== "",
    form.name.trim().length >= 3 && phoneOk,
    true,
  ][step];

  const quickDays = useMemo(() => {
    const days = [];
    const d = new Date();
    for (let i = 0; i < 14; i++) days.push(new Date(d.getFullYear(), d.getMonth(), d.getDate() + i));
    return days;
  }, []);

  const now = new Date();
  const isToday = form.date && form.date.toDateString() === now.toDateString();
  const slotDisabled = (t) => {
    const h = parseInt(t, 10);
    return busy.includes(t) || (isToday && h >= 11 && h <= now.getHours());
  };

  const dateLong = form.date
    ? form.date.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
    : "";
  const dayName = form.date ? form.date.toLocaleDateString("tr-TR", { weekday: "long" }) : "";

  const waMessage = `Merhaba Lunessa Spa Masaj,
Randevu talebi oluşturmak istiyorum.

Ad Soyad: ${form.name.trim()}
Telefon: ${form.phone.trim()}
Hizmet: ${form.service}
Tarih: ${dateLong}
Gün: ${dayName}
Saat: ${form.time}
Kişi Sayısı: ${form.guests}
Not: ${form.note.trim() || "-"}

Randevu uygunluğunu teyit eder misiniz?`;
  const waLink = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(waMessage)}`;

  const sendWhatsApp = () => {
    if (sending) return;
    setSending(true);
    trackEvent("appointment_whatsapp_click", {
      service: form.service,
      date: form.date ? toIso(form.date) : "",
      time: form.time,
    });
    window.open(waLink, "_blank");
    axios
      .post(`${API}/bookings`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        service: form.service,
        date: toIso(form.date),
        time: form.time,
        guests: form.guests,
        contact_preference: form.contact,
        note: form.note.trim(),
      })
      .catch(() => {})
      .finally(() => {
        setSending(false);
        setDone(true);
      });
  };

  const reset = () => {
    setForm(EMPTY);
    setBusy([]);
    setDone(false);
    setStep(0);
  };

  const summaryRows = [
    { label: "Hizmet", value: form.service, step: 0 },
    { label: "Gün", value: dayName, step: 1 },
    { label: "Tarih", value: dateLong, step: 1 },
    { label: "Saat", value: form.time, step: 2 },
    { label: "Ad Soyad", value: form.name.trim(), step: 3 },
    { label: "Telefon", value: form.phone.trim(), step: 3 },
    { label: "Kişi Sayısı", value: `${form.guests} kişi`, step: 3 },
    { label: "Not", value: form.note.trim() || "-", step: 3 },
  ];

  return (
    <div className="wizard" data-testid="booking-wizard">
      {done ? (
        <div className="wizard-success" role="status" data-testid="booking-success">
          <CheckCircle2 size={54} strokeWidth={1.4} />
          <h3>Randevu talebiniz oluşturuldu.</h3>
          <p data-testid="success-message">
            Kesin randevu onayı Lunessa Spa Masaj tarafından WhatsApp üzerinden yapılacaktır.
          </p>
          <button className="link-btn" onClick={reset} data-testid="new-booking-button">
            Yeni randevu talebi oluştur
          </button>
        </div>
      ) : (
        <>
          <div className="progress" data-testid="wizard-progress">
            <i style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>

          <ol className="wizard-steps" data-testid="wizard-steps">
            {STEPS.map((label, i) => (
              <li key={label} className={i === step ? "active" : i < step ? "complete" : ""} data-testid={`wizard-step-${i}`}>
                <span>{i < step ? <Check size={12} /> : i + 1}</span>
                <em>{label}</em>
              </li>
            ))}
          </ol>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              className="wizard-panel"
              initial={{ opacity: 0, x: 26 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -26 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
            >
              {step === 0 && (
                <div data-testid="step-service">
                  <h3>Hangi hizmeti almak istersiniz?</h3>
                  <div className="pick-grid">
                    {SERVICES.map((s, i) => (
                      <button
                        type="button"
                        key={s.name}
                        className={`pick-card${form.service === s.name ? " selected" : ""}`}
                        onClick={() => set({ service: s.name, time: "" })}
                        data-testid={`pick-service-${i}`}
                      >
                        <strong>{s.name}</strong>
                        <small><Clock3 size={12} /> {s.duration}</small>
                        {form.service === s.name && <i className="pick-check"><Check size={13} /></i>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div data-testid="step-date">
                  <h3>Hangi gün gelmek istersiniz?</h3>
                  <div className="mode-toggle">
                    <button
                      type="button"
                      className={calMode === "quick" ? "selected" : ""}
                      onClick={() => setCalMode("quick")}
                      data-testid="date-mode-quick"
                    >
                      <Zap size={14} /> Önümüzdeki Günler
                    </button>
                    <button
                      type="button"
                      className={calMode === "month" ? "selected" : ""}
                      onClick={() => setCalMode("month")}
                      data-testid="date-mode-month"
                    >
                      <CalendarDays size={14} /> Aylık Takvim
                    </button>
                  </div>
                  {calMode === "quick" ? (
                    <div className="day-strip" data-testid="day-strip">
                      {quickDays.map((d) => (
                        <button
                          type="button"
                          key={toIso(d)}
                          className={`day-card${form.date && d.toDateString() === form.date.toDateString() ? " selected" : ""}`}
                          onClick={() => set({ date: d, time: "" })}
                          data-testid={`day-card-${toIso(d)}`}
                        >
                          <small>{d.toLocaleDateString("tr-TR", { weekday: "long" })}</small>
                          <strong>{d.getDate()}</strong>
                          <small>{d.toLocaleDateString("tr-TR", { month: "long" })}</small>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <Calendar value={form.date} onChange={(d) => set({ date: d, time: "" })} />
                  )}
                </div>
              )}

              {step === 2 && (
                <div data-testid="step-time">
                  <h3>Saat seçin</h3>
                  <p className="panel-sub">
                    {dayName}, {dateLong} · Çalışma saatleri {HOURS}
                  </p>
                  <div className="time-grid">
                    {TIME_SLOTS.map((t) => (
                      <button
                        type="button"
                        key={t}
                        className={`time-slot${form.time === t ? " selected" : ""}${busy.includes(t) ? " busy" : ""}`}
                        disabled={slotDisabled(t)}
                        onClick={() => set({ time: t })}
                        data-testid={`time-slot-${t.replace(":", "")}`}
                      >
                        {t}
                        {busy.includes(t) && <small>Dolu</small>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="step-details">
                  <h3>İletişim bilgileriniz</h3>
                  <div className="form-grid">
                    <label className="field">
                      <span>Ad Soyad</span>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => set({ name: e.target.value })}
                        placeholder="Adınız ve soyadınız"
                        autoComplete="name"
                        data-testid="input-name"
                      />
                    </label>
                    <label className="field">
                      <span>Telefon</span>
                      <input
                        type="tel"
                        inputMode="tel"
                        value={form.phone}
                        onChange={(e) => set({ phone: e.target.value })}
                        placeholder="05XX XXX XX XX"
                        autoComplete="tel"
                        data-testid="input-phone"
                      />
                      {form.phone && !phoneOk && (
                        <small className="field-error" data-testid="phone-error">Geçerli bir telefon numarası girin (05XX XXX XX XX)</small>
                      )}
                    </label>
                    <div className="field">
                      <span>Kişi Sayısı</span>
                      <div className="pill-row" data-testid="guest-picker">
                        {[1, 2].map((n) => (
                          <button
                            type="button"
                            key={n}
                            className={`pill wide${form.guests === n ? " selected" : ""}`}
                            onClick={() => set({ guests: n })}
                            data-testid={`guests-${n}`}
                          >
                            {n} Kişi
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="field">
                      <span>İletişim Tercihi</span>
                      <div className="pill-row" data-testid="contact-picker">
                        <button
                          type="button"
                          className={`pill wide${form.contact === "whatsapp" ? " selected" : ""}`}
                          onClick={() => set({ contact: "whatsapp" })}
                          data-testid="contact-whatsapp"
                        >
                          WhatsApp
                        </button>
                        <button
                          type="button"
                          className={`pill wide${form.contact === "telefon" ? " selected" : ""}`}
                          onClick={() => set({ contact: "telefon" })}
                          data-testid="contact-telefon"
                        >
                          Telefon
                        </button>
                      </div>
                    </div>
                    <label className="field full">
                      <span>Not (opsiyonel)</span>
                      <textarea
                        rows={3}
                        value={form.note}
                        onChange={(e) => set({ note: e.target.value })}
                        placeholder="Eklemek istedikleriniz..."
                        data-testid="input-note"
                      />
                    </label>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div data-testid="step-summary">
                  <h3>Randevu özetiniz</h3>
                  <dl className="summary-list">
                    {summaryRows.map((row, i) => (
                      <div className="summary-row" key={row.label} data-testid={`summary-row-${i}`}>
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => setStep(row.step)}
                          aria-label={`${row.label} düzenle`}
                          data-testid={`edit-step-${row.step}`}
                        >
                          <Pencil size={13} />
                        </button>
                      </div>
                    ))}
                  </dl>
                  <p className="summary-note">
                    Gönderim sonrası WhatsApp açılır ve mesajınız hazır gelir. Bu bir randevu talebidir; kesin onay
                    salonumuz tarafından WhatsApp üzerinden yapılır.
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="wizard-nav">
            {step > 0 ? (
              <button className="btn-back" onClick={() => setStep(step - 1)} data-testid="wizard-back-button">
                <ArrowLeft size={17} /> Geri
              </button>
            ) : (
              <span />
            )}
            {step < 4 ? (
              <button className="btn-emerald big" onClick={() => setStep(step + 1)} disabled={!stepValid} data-testid="wizard-next-button">
                Devam Et <ArrowRight size={17} />
              </button>
            ) : (
              <button className="btn-gold big" onClick={sendWhatsApp} disabled={sending} data-testid="submit-booking-button">
                <MessageCircle size={18} /> {sending ? "Gönderiliyor..." : "Randevuyu WhatsApp'tan Gönder"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
